import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Footer } from 'src/shared';
import { useAppDispatch } from 'src/hooks';
import { getMedicalDeviceKDocuments } from 'src/reducers/user';
import { getMedicalDeviceStats } from 'src/services/api';
import { getAuthToken } from 'src/utils/auth';
import { UserSelectors } from 'src/reducers/user';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { MedicalDeviceCategories } from 'src/config/medicalDeviceCategories';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #111827 50%, #0b1220 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(2, 6, 23, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #1f2937;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid #334155;
  color: #ffffff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #475569;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  text-align: center;
  margin: 0;
  color: #ffffff;
`;

const RefreshButton = styled.button`
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  border: 1px solid #3b82f6;
  color: #ffffff;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid #334155;
  padding: 4vh 3vw;
  max-width: 80vw;
  min-width: 300px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  @media (max-width: 768px) {
    padding: 30px;
    max-width: 95vw;
  }
  @media (min-width: 1200px) {
    padding: 50px;
    max-width: 900px;
  }
`;

const Icon = styled.div`
  width: 90px;
  height: 90px;
  font-size: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 16px;
  color: #e5e7eb;
`;

const SectionDesc = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 24px;
  color: rgba(255,255,255,0.85);
`;

const SurgicalPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { profile } = UserSelectors();
  const isLoggedIn = Boolean(getAuthToken() && profile);
  const numDocsRender = 6;

  // Filters from URL
  const category = searchParams.get('category'); // expected "2"
  const subCategory = searchParams.get('subCategory'); // expected "B"

  // State
  const [medicalDeviceDocs, setMedicalDeviceDocs] = useState<Array<any>>();
  const [isLoadingDocs, setIsLoadingDocs] = useState<boolean>(true);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState<boolean>(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('');
  const [currentPdfTitle, setCurrentPdfTitle] = useState<string>('');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [seenK, setSeenK] = useState<Set<string>>(new Set());
  const [companyFilter, setCompanyFilter] = useState<string | null>(null);
  // Stats state
  const [stats, setStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      if (category) {
        setIsLoadingDocs(true);
        setDocsError(null);
        setSeenK(new Set());
        setOffset(0);
        try {
          const resp: any = await dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender, category, offset: 0, /* @ts-ignore */ company: companyFilter || undefined } as any));
          const data_tuples = resp?.payload?.data?.data_tuples || [];
          const uniq = data_tuples.filter((d: any) => d?.k_number);
          setMedicalDeviceDocs(uniq);
          setSeenK(new Set(uniq.map((d: any) => d.k_number)));
          setOffset(numDocsRender);
        } catch (error: any) {
          setDocsError(error?.message || 'Failed to load documents');
        } finally {
          setIsLoadingDocs(false);
        }
        // Fetch stats for this category
        setIsLoadingStats(true);
        setStatsError(null);
        setStats(null);
        try {
          const statsResp: any = await getMedicalDeviceStats(category || undefined, undefined);
          setStats(statsResp?.data || null);
        } catch (e: any) {
          setStatsError(e?.message || 'Failed to load stats');
        } finally {
          setIsLoadingStats(false);
        }
      } else {
        setIsLoadingDocs(true);
        try {
          const resp: any = await dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender }));
          const data_tuples = resp?.payload?.data?.data_tuples || [];
          setMedicalDeviceDocs(data_tuples);
        } catch (error: any) {
          setDocsError(error?.message || 'Failed to load documents');
        } finally {
          setIsLoadingDocs(false);
        }
      }
    };
    init();
  }, [dispatch, category, companyFilter]);

  const handleRefresh = async () => {
    if (!category) return;
    setIsLoadingDocs(true);
    setDocsError(null);
    try {
      let nextOffset = offset;
      let attempts = 0;
      let nextBatch: any[] = [];
      while (attempts < 3) {
        const resp: any = await dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender, category, offset: nextOffset, /* @ts-ignore */ company: companyFilter || undefined } as any));
        const data_tuples = resp?.payload?.data?.data_tuples || [];
        const filtered = data_tuples.filter((d: any) => d?.k_number && !seenK.has(d.k_number));
        if (filtered.length >= Math.min(numDocsRender, data_tuples.length)) {
          nextBatch = filtered.slice(0, numDocsRender);
          nextOffset += numDocsRender;
          break;
        }
        nextOffset += numDocsRender;
        attempts += 1;
        if (!nextBatch.length && filtered.length) {
          nextBatch = filtered.slice(0, numDocsRender);
        }
      }
      if (nextBatch.length) {
        setMedicalDeviceDocs(nextBatch);
        const updated = new Set(seenK);
        nextBatch.forEach((d: any) => updated.add(d.k_number));
        setSeenK(updated);
        setOffset(nextOffset);
      } else {
        // Wrap around: reset offset and seen set, fetch from start with same filters
        const resp: any = await dispatch(getMedicalDeviceKDocuments({ numDocs: numDocsRender, category, offset: 0, /* @ts-ignore */ company: companyFilter || undefined } as any));
        const data_tuples = resp?.payload?.data?.data_tuples || [];
        const uniq = data_tuples.filter((d: any) => d?.k_number);
        if (uniq.length > 0) {
          setMedicalDeviceDocs(uniq);
          setSeenK(new Set(uniq.map((d: any) => d.k_number)));
          setOffset(numDocsRender);
        } else {
          setDocsError('No new documents available');
        }
      }
    } catch (error: any) {
      setDocsError(error?.message || 'Failed to refresh documents');
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const openPdfModal = (pdfUrl: string, title: string) => {
    setCurrentPdfUrl(pdfUrl);
    setCurrentPdfTitle(title);
    setPdfError(null);
    setPdfModalOpen(true);
  };

  const closePdfModal = () => {
    setPdfModalOpen(false);
    setCurrentPdfUrl('');
    setCurrentPdfTitle('');
    setPdfError(null);
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/medical_imaging')}>
          ← Back to Medical Imaging
        </BackButton>
        <Title>Surgical & Interventional Systems</Title>
        <div style={{ width: '180px', display: 'flex', justifyContent: 'flex-end' }}>
          <RefreshButton onClick={handleRefresh}>
            ↻ Refresh
          </RefreshButton>
        </div>
      </Header>

      <Content>
        <HeroCard>
          <Icon>🤖</Icon>
          <SectionTitle>Robotic, Navigation, and Interventional Systems</SectionTitle>
          {/* Stats Block */}
          {isLoadingStats ? (
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>Loading stats…</div>
          ) : statsError ? (
            <div style={{ color: '#fca5a5', fontSize: '0.95rem' }}>❌ {statsError}</div>
          ) : stats ? (
            <div style={{ width: '100%' }}>
              {/* Top Row: Totals */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 16,
                marginTop: 8,
                marginBottom: 16
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))',
                  border: '1px solid rgba(59,130,246,0.3)',
                  borderRadius: 12,
                  padding: '14px 16px',
                }}>
                  <div style={{ color: '#93c5fd', fontSize: 12, letterSpacing: 0.3, marginBottom: 6 }}>TOTAL K DOCUMENTS</div>
                  <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 800 }}>
                    {Number(stats.total_docs || 0).toLocaleString()}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 4 }}>
                    Category {category || 'B'} • {stats?.year_span?.min_year || '—'}–{stats?.year_span?.max_year || '—'}
                  </div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: 12,
                  padding: '14px 16px',
                }}>
                  <div style={{ color: '#6ee7b7', fontSize: 12, letterSpacing: 0.3, marginBottom: 6 }}>TOP PRODUCT CODES (LAST 3Y)</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(stats.top_product_codes_recent || []).map((p: any, idx: number) => (
                      <span key={idx} style={{
                        fontSize: 12,
                        color: '#10b981',
                        background: 'rgba(16,185,129,0.15)',
                        border: '1px solid rgba(16,185,129,0.35)',
                        padding: '6px 10px',
                        borderRadius: 999,
                        fontWeight: 700
                      }}>
                        {p.product_code}: {p.count}
                      </span>
                    ))}
                    {(!stats.top_product_codes_recent || stats.top_product_codes_recent.length === 0) && (
                      <span style={{ color: 'rgba(255,255,255,0.75)' }}>No recent data</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Companies */}
              <div style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(148,163,184,0.35)',
                borderRadius: 12,
                padding: '14px 16px',
                textAlign: 'left'
              }}>
                <div style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Top 4 Companies by K-count</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 8, columnGap: 12 }}>
                  {(stats.top_companies || []).map((c: any, idx: number) => (
                    <React.Fragment key={idx}>
                      <button
                        onClick={() => setCompanyFilter(c.name)}
                        title={`Filter by ${c.name}`}
                        style={{
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          color: '#cbd5e1',
                          fontSize: 14,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: 999,
                          border: '1px solid rgba(148,163,184,0.35)',
                          background: 'rgba(255,255,255,0.05)'
                        }}>
                          {c.name || 'Unknown'}
                        </span>
                      </button>
                      <div style={{ color: '#93c5fd', fontWeight: 800, fontSize: 14 }}>{c.count}</div>
                    </React.Fragment>
                  ))}
                  {(!stats.top_companies || stats.top_companies.length === 0) && (
                    <div style={{ color: 'rgba(255,255,255,0.75)' }}>No company data</div>
                  )}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, marginTop: 10 }}>
                  Company names are normalized (e.g., variants like “Inc.”, “LLC”, etc. are merged).
                </div>
                {companyFilter && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#93c5fd', fontSize: 13 }}>Filtering by company: <strong>{companyFilter}</strong></span>
                    <button
                      onClick={() => setCompanyFilter(null)}
                      style={{
                        background: 'rgba(239,68,68,0.15)',
                        color: '#fecaca',
                        border: '1px solid rgba(239,68,68,0.35)',
                        borderRadius: 999,
                        padding: '4px 10px',
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <SectionDesc>
              Explore FDA-cleared surgical robots, interventional guidance systems, surgical navigation,
              and surgical lasers. Below are top recent clearances filtered for this category.
            </SectionDesc>
          )}
        </HeroCard>

        {/* FDA Medical Device Clearance Documents */}
        <div style={{
          width: '100%',
          maxWidth: '1200px',
          marginTop: '40px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.05) 100%)',
          borderRadius: '16px',
          padding: '30px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)'
        }}>
          <h2 style={{
            fontSize: '1.6rem',
            fontWeight: 700,
            marginBottom: 20,
            color: '#ffffff',
            textAlign: 'center'
          }}>
            📊 FDA Medical Device Clearance Documents
            {category && subCategory && (
              <span style={{
                fontSize: '0.9rem',
                fontWeight: 500,
                color: '#93c5fd',
                marginLeft: 10,
                display: 'block',
                marginTop: 5
              }}>
                {(() => {
                  const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (category || ''));
                  const sub = cat?.subCategories.find(s => s.code === subCategory || '');
                  if (!cat) return '🔍 Filtered';
                  if (sub) {
                    if (sub.code === cat.code) {
                      return `🔍 Filtered: ${cat.title}`;
                    }
                    return `🔍 Filtered: ${cat.title} • ${sub.title}`;
                  }
                  return `🔍 Filtered: ${cat.title}`;
                })()}
              </span>
            )}
          </h2>
          <p style={{
            fontSize: '1rem',
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            {category && subCategory
              ? (() => {
                  const cat = MedicalDeviceCategories.find(c => c.categoryNumber === (category || ''));
                  const sub = cat?.subCategories.find(s => s.code === (subCategory || ''));
                  if (!cat) return `Showing ${medicalDeviceDocs?.length || 0} documents`;
                  if (sub) {
                    if (sub.code === cat.code) {
                      return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                    }
                    return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title} • ${sub.title}`;
                  }
                  return `Showing ${medicalDeviceDocs?.length || 0} documents filtered by ${cat.title}`;
                })()
              : 'Latest FDA medical device clearance documents from our database'}
          </p>

          {isLoadingDocs ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.8)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>⏳</div>
              Loading documents...
            </div>
          ) : docsError ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#fca5a5' }}>
              ❌ Error: {docsError}
            </div>
          ) : medicalDeviceDocs && medicalDeviceDocs.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '25px',
              marginBottom: '30px'
            }}>
              {medicalDeviceDocs.map((doc: any, index: number) => (
                <div key={index} style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(148,163,184,0.4)',
                  borderRadius: 16,
                  padding: 25,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px rgba(0, 0, 0, 0.5)';
                  e.currentTarget.style.borderColor = '#93c5fd';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(148,163,184,0.4)';
                }}
                >
                  {/* Document Icon */}
                  <div style={{ fontSize: '3rem', textAlign: 'center', marginBottom: 15 }}>📋</div>

                  {/* K Number Badge */}
                  <div
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: 20,
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      textAlign: 'center',
                      marginBottom: 15,
                      display: 'inline-block',
                      width: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#2563eb'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = '#3b82f6'; }}
                    onClick={() => {
                      if (doc.pdf_url) {
                        const deviceDisplayName = doc.company_name
                          ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                          : doc.device_name || 'Device name not available';
                        openPdfModal(doc.pdf_url, `${doc.k_number} - ${deviceDisplayName}`);
                      }
                    }}
                  >
                    📄 {doc.k_number || `Document ${index + 1}`}
                  </div>

                  {/* Device Name */}
                  <h3 style={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    marginBottom: 12,
                    color: 'rgba(255,255,255,0.9)',
                    textAlign: 'center',
                    lineHeight: 1.4
                  }}>
                    {doc.company_name
                      ? `${doc.device_name || 'Unknown Device'} from ${doc.company_name}`
                      : doc.device_name || 'Device name not available'}
                  </h3>

                  {/* Tags */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    justifyContent: 'center',
                    marginBottom: 12
                  }}>
                    {doc.device_class && (
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#93c5fd',
                        backgroundColor: 'rgba(59,130,246,0.15)',
                        padding: '4px 8px',
                        borderRadius: 12,
                        fontWeight: 500,
                        border: '1px solid rgba(59,130,246,0.3)'
                      }}>
                        {doc.device_class}
                      </span>
                    )}
                    <span style={{
                      fontSize: '0.7rem',
                      color: '#34d399',
                      backgroundColor: 'rgba(52,211,153,0.2)',
                      padding: '4px 8px',
                      borderRadius: 12,
                      fontWeight: 500,
                      border: '1px solid rgba(52,211,153,0.3)'
                    }}>
                      {doc.document_type === '510(k) Premarket Notification' ? '510(k)' :
                       doc.document_type === 'PMA' ? 'PMA' :
                       doc.document_type === 'De Novo' ? 'De Novo' : '510(k)'}
                    </span>
                    {doc.is_samd && (
                      <span style={{
                        fontSize: '0.7rem',
                        color: '#f87171',
                        backgroundColor: 'rgba(248,113,113,0.2)',
                        padding: '4px 8px',
                        borderRadius: 12,
                        fontWeight: 500,
                        border: '1px solid rgba(248,113,113,0.3)'
                      }}>
                        SaMD
                      </span>
                    )}
                    {(() => {
                      const dt: any = (doc.clearance_date || doc.decision_date || doc.submission_date);
                      const s = dt ? String(dt) : '';
                      const match = s.match(/\b(\d{4})\b/);
                      const yearNum = match ? parseInt(match[1], 10) : NaN;
                      const currentYear = new Date().getFullYear();
                      const isValidYear = !isNaN(yearNum) && yearNum >= 1970 && yearNum <= currentYear + 1;
                      return isValidYear ? (
                        <span style={{
                          fontSize: '0.7rem',
                          color: '#cbd5e1',
                          backgroundColor: 'rgba(148,163,184,0.2)',
                          padding: '4px 8px',
                          borderRadius: 12,
                          fontWeight: 500,
                          border: '1px solid rgba(148,163,184,0.35)'
                        }}>
                          {yearNum}
                        </span>
                      ) : null;
                    })()}
                  </div>

                  {/* Intended Use */}
                  {doc.intended_use && (
                    <p style={{
                      fontSize: '0.75rem',
                      color: 'rgba(255,255,255,0.7)',
                      textAlign: 'center',
                      marginBottom: 20,
                      lineHeight: 1.3,
                      fontStyle: 'italic',
                      maxHeight: 40,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      <strong>Intended use:</strong> {doc.intended_use.length > 60
                        ? `${doc.intended_use.substring(0, 60)}...`
                        : doc.intended_use}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.8)' }}>
              No documents found
            </div>
          )}
        </div>
      </Content>

      {/* PDF Modal */}
      {pdfModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 10000,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div style={{
            width: '100%', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            backgroundColor: '#0b1220', borderRadius: 8, overflow: 'hidden'
          }}>
            <div style={{
              padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              borderBottom: '1px solid #1f2937'
            }}>
              <h3 style={{ color: '#ffffff', margin: 0, fontSize: '1rem' }}>{currentPdfTitle}</h3>
              <button onClick={closePdfModal} style={{
                background: 'transparent', border: 'none', color: '#ffffff', fontSize: 24, cursor: 'pointer', padding: '0 10px'
              }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#111827' }}>
              {currentPdfUrl && (
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={currentPdfUrl}
                    onDocumentLoad={() => setPdfError(null)}
                    renderPage={(props) => (
                      <div>
                        {props.canvasLayer.children}
                        {props.textLayer && props.textLayer.children}
                      </div>
                    )}
                  />
                </Worker>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </Container>
  );
};

export default SurgicalPage;
